<div>
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">Dashboard metrics could not be loaded. Try refreshing the page.</div>
    }

    <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
        <div>
            <label class="form-label small text-muted mb-1" for="pco-dashboard-job">Job</label>
            <select id="pco-dashboard-job" class="form-select form-select-sm" style="min-width: 18rem" [(ngModel)]="selectedJob" (ngModelChange)="onFilterChange()">
                <option value="">All jobs</option>
                @for (job of metrics.filterJobs; track job.job) {
                    <option [value]="job.job">{{ job.display }}</option>
                }
            </select>
        </div>
        <div class="form-check mb-1">
            <input id="pco-dashboard-hot" class="form-check-input" type="checkbox" [(ngModel)]="hotOnly" (ngModelChange)="onFilterChange()" />
            <label class="form-check-label" for="pco-dashboard-hot">Hot only</label>
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary mb-1" (click)="refresh()">Refresh</button>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div
                class="card h-100 border-primary border-opacity-25"
                [class.pco-kpi-clickable]="metrics.kpi.openCount > 0"
                role="button"
                (click)="drill(openRecordIds(), 'Open pending change orders')"
            >
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Open PCOs</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.openCount }}</div>
                    <div class="small text-muted">{{ formatUsd(metrics.kpi.openApproxCost) }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-danger border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Hot items</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.hotCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div
                class="card h-100 border-warning border-opacity-50"
                [class.pco-kpi-clickable]="metrics.kpi.awaitingMyActionCount > 0"
                role="button"
                (click)="drill(metrics.kpi.awaitingMyActionIds, 'Awaiting my action')"
            >
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Awaiting my action</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.awaitingMyActionCount }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div
                class="card h-100 border-primary border-opacity-25"
                [class.pco-kpi-clickable]="metrics.kpi.awaitingInternalApprovalCount > 0"
                role="button"
                (click)="drill(metrics.kpi.awaitingInternalApprovalIds, 'Awaiting internal approval')"
            >
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Awaiting internal approval</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.awaitingInternalApprovalCount }}</div>
                    <div class="small text-muted">{{ formatUsd(metrics.kpi.awaitingInternalApprovalCost) }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div
                class="card h-100 border-warning border-opacity-50"
                [class.pco-kpi-clickable]="metrics.kpi.awaitingOwnerCount > 0"
                role="button"
                (click)="drill(metrics.kpi.awaitingOwnerIds, 'Awaiting owner')"
            >
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Awaiting owner</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.awaitingOwnerCount }}</div>
                    <div class="small text-muted">
                        {{ formatUsd(metrics.kpi.awaitingOwnerCost) }}
                        @if (metrics.kpi.awaitingOwnerCount > 0) {
                            · {{ metrics.kpi.awaitingOwnerAverageDays | number: '1.0-0' }} days avg
                        }
                    </div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div
                class="card h-100 border-danger border-opacity-50"
                [class.pco-kpi-clickable]="metrics.kpi.stalledCount > 0"
                role="button"
                (click)="drill(metrics.kpi.stalledIds, 'Stalled pending change orders')"
            >
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Stalled</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.stalledCount }}</div>
                    <div class="small text-muted">Past stage aging threshold</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Executed / imported this month</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.executedThisMonth }} / {{ metrics.kpi.importedThisMonth }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Avg cycle time (90 days)</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.averageCycleDaysLast90 | number: '1.0-1' }}</div>
                    <div class="small text-muted">{{ metrics.kpi.cycleTimeSampleCount }} completed</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">By job</h5>
                    <p class="small text-muted">Open pending change orders rolled up by project, with the job PM and JCA.</p>
                    @if (metrics.byJob.length === 0) {
                        <p class="text-muted small mb-0">No open pending change orders.</p>
                    } @else {
                        <div class="table-responsive pco-table-scroll">
                            <table class="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th>PM</th>
                                        <th>JCA</th>
                                        <th class="text-end">Open</th>
                                        <th class="text-end">$</th>
                                        <th class="text-end">Hot</th>
                                        <th class="text-end">Awaiting owner</th>
                                        <th class="text-end">Oldest (days)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.byJob; track row.job) {
                                        <tr>
                                            <td>
                                                <button type="button" class="btn btn-link btn-sm p-0" (click)="drill(row.recordIds, row.jobDisplay)">
                                                    {{ row.jobDisplay }}
                                                </button>
                                            </td>
                                            <td>{{ row.pmName }}</td>
                                            <td>{{ row.jcaName }}</td>
                                            <td class="text-end">{{ row.openCount }}</td>
                                            <td class="text-end">{{ formatUsd(row.openApproxCost) }}</td>
                                            <td class="text-end">{{ row.hotCount }}</td>
                                            <td class="text-end">{{ row.awaitingOwnerCount }}</td>
                                            <td class="text-end">{{ row.oldestDays }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Pipeline</h5>
                    <p class="small text-muted">Open records by current workflow stage. Click a stage to open those records.</p>
                    @if (metrics.pipeline.length === 0) {
                        <p class="text-muted small mb-0">No open pending change orders.</p>
                    } @else {
                        @for (bucket of metrics.pipeline; track bucket.stage) {
                            <button
                                type="button"
                                class="btn btn-link text-decoration-none text-body pco-queue-toggle w-100 px-0 py-1"
                                [disabled]="bucket.count === 0"
                                (click)="drill(bucket.recordIds, bucket.stage)"
                            >
                                <div class="d-flex align-items-center gap-2">
                                    <div class="pco-stage-label text-start">
                                        {{ bucket.stage }}
                                        @if (bucket.stalledCount > 0) {
                                            <span class="badge text-bg-danger ms-1">{{ bucket.stalledCount }} stalled</span>
                                        }
                                    </div>
                                    <div class="flex-grow-1 pco-funnel-track">
                                        <div class="pco-funnel-bar" [style.width.%]="(bucket.count / maxPipelineCount()) * 100"></div>
                                    </div>
                                    <div class="text-nowrap small">{{ bucket.count }} · {{ formatUsd(bucket.approxCostTotal) }}</div>
                                </div>
                            </button>
                        }
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">What's next and who owns it</h5>
                    <p class="small text-muted">Open records grouped by the person or role responsible for the next step. Expand a group to see the records.</p>
                    @if (metrics.actionQueue.length === 0) {
                        <p class="text-muted small mb-0">No open action items.</p>
                    } @else {
                        <div class="table-responsive pco-table-scroll">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Responsible</th>
                                        <th>Stage</th>
                                        <th class="text-end">Count</th>
                                        <th class="text-end">$</th>
                                        <th class="text-end">Oldest (days)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (group of metrics.actionQueue; track group.key) {
                                        <tr>
                                            <td>
                                                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleGroup(group.key)">
                                                    {{ isExpanded(group.key) ? 'Hide' : 'Show' }}
                                                </button>
                                            </td>
                                            <td>
                                                <button type="button" class="btn btn-link btn-sm p-0" (click)="drill(group.recordIds, group.responsibleName + ' — ' + group.stage)">
                                                    {{ group.responsibleName }}
                                                </button>
                                                <div class="small text-muted">{{ group.responsibleRole }}</div>
                                            </td>
                                            <td>{{ group.stage }}</td>
                                            <td class="text-end">{{ group.count }}</td>
                                            <td class="text-end">{{ formatUsd(group.approxCostTotal) }}</td>
                                            <td class="text-end">{{ group.oldestDays }}</td>
                                        </tr>
                                        @if (isExpanded(group.key)) {
                                            @for (record of group.records; track record.id) {
                                                <tr>
                                                    <td></td>
                                                    <td colspan="5">
                                                        <button type="button" class="btn btn-link btn-sm p-0" (click)="openRecord(record.id)">
                                                            {{ record.jobDisplay }}
                                                            @if (record.pcoNumber) {
                                                                · {{ record.pcoNumber }}
                                                            }
                                                            @if (record.description) {
                                                                — {{ record.description }}
                                                            }
                                                        </button>
                                                        <span class="small text-muted ms-2">{{ record.daysInStage }} days · {{ formatUsd(record.approxCost) }}</span>
                                                        @if (record.isHot) {
                                                            <span class="badge text-bg-danger ms-1">Hot</span>
                                                        }
                                                    </td>
                                                </tr>
                                            }
                                        }
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card border-danger border-opacity-50">
                <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h5 class="card-title mb-0">Stalled / aging</h5>
                        <span class="badge rounded-pill text-bg-danger">{{ metrics.stalled.length }}</span>
                    </div>
                    <p class="small text-muted">Records past the aging threshold for their current stage.</p>
                    @if (metrics.stalled.length === 0) {
                        <p class="text-muted small mb-0">No stalled pending change orders.</p>
                    } @else {
                        <div class="table-responsive pco-table-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th>PCO#</th>
                                        <th>Description</th>
                                        <th>Stage</th>
                                        <th>Responsible</th>
                                        <th class="text-end">Days</th>
                                        <th class="text-end">$</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.stalled; track row.id) {
                                        <tr class="pco-stalled-row">
                                            <td>
                                                <button type="button" class="btn btn-link btn-sm p-0" (click)="openRecord(row.id)">{{ row.jobDisplay }}</button>
                                            </td>
                                            <td>{{ row.pcoNumber }}</td>
                                            <td>{{ row.description }}</td>
                                            <td>{{ row.stage }}</td>
                                            <td>{{ row.responsibleName }}</td>
                                            <td class="text-end">{{ row.daysInStage }}</td>
                                            <td class="text-end">{{ formatUsd(row.approxCost) }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Cycle time by stage</h5>
                    <p class="small text-muted">Average and median days in each step for records completed in the last 12 months.</p>
                    @if (metrics.stageDurations.length === 0) {
                        <p class="text-muted small mb-0">No completed records in this period.</p>
                    } @else {
                        @for (row of metrics.stageDurations; track row.stage) {
                            <div class="mb-2">
                                <div class="d-flex justify-content-between small">
                                    <span>{{ row.stage }}</span>
                                    <span>{{ row.averageDays | number: '1.0-1' }} avg · {{ row.medianDays | number: '1.0-1' }} med ({{ row.sampleCount }})</span>
                                </div>
                                <div class="pco-duration-track">
                                    <div class="pco-duration-bar" [style.width.%]="durationBarWidth(row.averageDays)"></div>
                                </div>
                            </div>
                        }
                    }
                </div>
            </div>
        </div>
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Monthly trend</h5>
                    <p class="small text-muted">Initiated, owner executed, and imported into Vista over the last 12 months.</p>
                    @if (metrics.monthlyTrend.length === 0) {
                        <p class="text-muted small mb-0">No activity in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 pco-bar-wrap">
                            @for (m of metrics.monthlyTrend; track m.label) {
                                <div class="flex-fill text-center">
                                    <div class="d-flex align-items-end justify-content-center gap-0">
                                        <div
                                            class="pco-bar pco-bar-initiated rounded-top"
                                            [style.height.px]="(m.initiated / maxTrendCount()) * 120 || 4"
                                            [title]="'Initiated: ' + m.initiated"
                                        ></div>
                                        <div
                                            class="pco-bar pco-bar-executed rounded-top"
                                            [style.height.px]="(m.ownerExecuted / maxTrendCount()) * 120 || 4"
                                            [title]="'Owner executed: ' + m.ownerExecuted"
                                        ></div>
                                        <div
                                            class="pco-bar pco-bar-imported rounded-top"
                                            [style.height.px]="(m.importedIntoVista / maxTrendCount()) * 120 || 4"
                                            [title]="'Imported: ' + m.importedIntoVista"
                                        ></div>
                                    </div>
                                    <div class="small text-muted mt-1" style="font-size: 10px">{{ m.label }}</div>
                                </div>
                            }
                        </div>
                        <div class="d-flex flex-wrap gap-3 small mt-2">
                            <span><span class="badge text-bg-primary">&nbsp;</span> Initiated</span>
                            <span><span class="badge text-bg-success">&nbsp;</span> Owner executed</span>
                            <span><span class="badge" style="background: #6f42c1">&nbsp;</span> Imported</span>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Work type</h5>
                    @for (slice of metrics.composition.byWorkType; track slice.label) {
                        <div class="d-flex justify-content-between small mb-1">
                            <span>{{ slice.label }}</span>
                            <span>{{ slice.count }} · {{ formatUsd(slice.approxCostTotal) }}</span>
                        </div>
                    }
                </div>
            </div>
        </div>
        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Pricing method</h5>
                    @for (slice of metrics.composition.byPricingMethod; track slice.label) {
                        <div class="d-flex justify-content-between small mb-1">
                            <span>{{ slice.label }}</span>
                            <span>{{ slice.count }} · {{ formatUsd(slice.approxCostTotal) }}</span>
                        </div>
                    }
                </div>
            </div>
        </div>
        <div class="col-lg-4">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Initiated by</h5>
                    @for (slice of metrics.composition.byInitiatedBy; track slice.label) {
                        <div class="d-flex justify-content-between small mb-1">
                            <span>{{ slice.label }}</span>
                            <span>{{ slice.count }} · {{ formatUsd(slice.approxCostTotal) }}</span>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Risk and data-quality flags</h5>
                    <p class="small text-muted">Click a flag to open the matching records.</p>
                    @if (metrics.riskFlags.length === 0) {
                        <p class="text-muted small mb-0">No risk flags.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Flag</th>
                                        <th class="text-end">Count</th>
                                        <th class="text-end">$</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (flag of metrics.riskFlags; track flag.key) {
                                        <tr>
                                            <td>
                                                @if (flag.count > 0) {
                                                    <button type="button" class="btn btn-link btn-sm p-0" (click)="drill(flag.recordIds, flag.label)">
                                                        {{ flag.label }}
                                                    </button>
                                                } @else {
                                                    {{ flag.label }}
                                                }
                                            </td>
                                            <td class="text-end">{{ flag.count }}</td>
                                            <td class="text-end">{{ formatUsd(flag.approxCostTotal) }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
